// PreChemoCheckup.jsx - Step 1 Component
import React, { useContext, useEffect, useRef, useState } from "react";
import { TextField } from "@mui/material";
import { CommonContext } from "../../../../context/CommonContext";
import { AuthContext } from "../../../../context/AuthContext";
import { mapPreChemoCheckupResponse, preChemoCheckupInitialValues } from "../../../../schema/preChemoCheckupInitialValues";
import { apisPath, paths } from "../../../../utils/path";
import { authHttp } from "../../../../services/commonHttpService";
import { Formik } from "formik";
import { preChemoCheckupSchema } from "../../../../schema/preChemoCheckupSchema";
import { formatDate, shouldAllowChemoAdmin } from "../../../../helpers/commonHelper";
import { useNavigate } from "react-router-dom";

const PreChemoCheckup = ({
    patientId,
    submitForm,
    formResult,
    currentStep,
    isRedirected,
    chemoSessionDate,
    upcomingOnMin
}) => {
    const navigate = useNavigate();
    const { setIsLoading, setAlertState, setSnackState } = useContext(CommonContext);
    const { userId } = useContext(AuthContext);

    const [formValues, setFormValues] = useState(preChemoCheckupInitialValues);
    const [isFocus, setIsFocus] = useState(false);
    const [patientRecord, setPatientRecord] = useState(0);
    const formRef = useRef(null);

    const labTestLabels = [
        { key: "hemoglobin", label: "Hemoglobin (g/dL)", apiKey: "haemoglobin" },
        { key: "totalLeukocyte", label: "Total Leucocyte Count (cells/mm³)", apiKey: "leucocyte" },
        { key: "absoluteNeutrophil", label: "Absolute Neutrophil Count (ANC)", apiKey: "neutrophil" },
        { key: "plateletCount", label: "Platelet Count (lakh/µL)", apiKey: "platelet" },
        { key: "serumCreatinine", label: "Serum Creatinine (mg/dL)", apiKey: "serum_creatinine" },
        { key: "bloodUreaNitrogen", label: "Blood Urea Nitrogen", apiKey: "blood_urea_nitrogen" },
        { key: "serumBilirubin", label: "Serum Bilirubin", apiKey: "serum_bilirubin" },
        { key: "sgot", label: "SGOT", apiKey: "sgot" },
        { key: "sgpt", label: "SGPT", apiKey: "sgpt" },
        { key: "sodium", label: "Sodium", apiKey: "sodium" },
        { key: "potassium", label: "Potassium", apiKey: "potassium" },
        { key: "cancerMarkers", label: "Cancer markers", apiKey: "cancer_markers" },
    ];

    // Trigger submit from parent
    useEffect(() => {
        if (submitForm) {
            formRef.current?.handleSubmit();

            if (Object.keys(formRef.current?.errors || {}).length > 0) {
                setIsFocus(true);
            }

            formResult(false); // parent resets submitForm
        }
    }, [submitForm]);

    // Focus & alert on first error
    useEffect(() => {
        if (isFocus && Object.keys(formRef.current?.errors || {}).length) {
            const keys = Object.keys(formRef.current.errors);
            document.getElementsByName(keys[0])[0]?.focus();

            setSnackState({
                open: true,
                message: Object.values(formRef.current.errors)[0],
                severity: "error",
            });

            setIsFocus(false);
        }
    }, [isFocus]);

    // Fetch saved pre-chemo checkup data
    useEffect(() => {
        if (currentStep === 0 && patientId) {
            fetchStepData();
        }
    }, [currentStep, patientId]);

    const fetchStepData = async () => {
        setIsLoading(true);
        try {
            const response = await authHttp.get(
                `${apisPath?.getMoPatientTreatmentStep}/${patientId}/1`
            );

            if (response?.data?.data) {
                const mappedValues = mapPreChemoCheckupResponse(response?.data?.data);
                setFormValues(mappedValues);

                // Store patient_record for session_number calculation
                setPatientRecord(response?.data?.data?.patient_record || 0);
            }
        } catch (error) {
            // If 404 → no previous data (expected)
            if (error?.response?.status === 404) {
                console.log("No previous pre-chemo checkup data - starting fresh.");
                setPatientRecord(0);
            } else {
                // Real error
                setSnackState({
                    open: true,
                    message: error?.response?.data?.message || "Something went wrong",
                    severity: "error",
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // FORM SUBMIT HANDLER
    const submitHandler = async (values) => {
        setIsLoading(true);

        try {
            const payload = {
                patient_id: Number(patientId),
                user_id: Number(userId),
                check_up_date: values?.checkupDate,
                ecog_ps: Number(values?.ecogStatus),
                haemoglobin: Number(values?.hemoglobin),
                haemoglobin_date: values?.hemoglobinDate,
                leucocyte: Number(values?.totalLeukocyte),
                leucocyte_date: values?.totalLeukocyteDate,
                neutrophil: Number(values?.absoluteNeutrophil),
                neutrophil_date: values?.absoluteNeutrophilDate,
                platelet: Number(values?.plateletCount),
                platelet_date: values?.plateletCountDate,
                serum_creatinine: Number(values?.serumCreatinine),
                serum_creatinine_date: values?.serumCreatinineDate,
                blood_urea_nitrogen: Number(values?.bloodUreaNitrogen),
                blood_urea_nitrogen_date: values?.bloodUreaNitrogenDate,
                serum_bilirubin: Number(values?.serumBilirubin),
                serum_bilirubin_date: values?.serumBilirubinDate,
                sgot: Number(values?.sgot),
                sgot_date: values?.sgotDate,
                sgpt: Number(values?.sgpt),
                sgpt_date: values?.sgptDate,
                sodium: Number(values?.sodium),
                sodium_date: values?.sodiumDate,
                potassium: Number(values?.potassium),
                potassium_date: values?.potassiumDate,
                cancer_markers: Number(values?.cancerMarkers),
                cancer_markers_date: values?.cancerMarkersDate,
                version: 1,
            };

            // Only include session_number if user was redirected (not navigated via Previous button)
            if (isRedirected) {
                payload.session_number = patientRecord + 1;
            }

            await authHttp.post(`${apisPath?.moPatientTreatmentStep}/1`, payload);

            // Check if chemotherapy administration is allowed based on session date
            const isAllowed = shouldAllowChemoAdmin(chemoSessionDate);

            if (isAllowed) {
                // Session date allows chemo admin, show success and proceed
                setSnackState({
                    open: true,
                    message: "Pre-Chemotherapy checkup saved successfully!",
                    severity: "success",
                });

                formResult(true); // tell parent step successful, proceed to next step
            } else {
                // Session date is in the future, show blocking alert and redirect
                setAlertState({
                    open: true,
                    title: `Pre-Chemotherapy checkup saved successfully! but you can only submit ChemoAdministration data after the treatment date or the day of treatment ie. ${formatDate(upcomingOnMin)}`,
                    btnTxt: "CLOSE",
                    status: "fail",
                    onClose: () => {
                        navigate(`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`);
                    }
                });

                formResult(false); // Don't proceed to next step
            }
        } catch (err) {
            setSnackState({
                open: true,
                message: err?.response?.data?.message || "Failed to save checkup data",
                severity: "error",
            });

            formResult(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Formik
            initialValues={formValues}
            enableReinitialize
            validationSchema={preChemoCheckupSchema}
            onSubmit={submitHandler}
            innerRef={formRef}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
            }) => (
                <div>
                    {/* Date and ECOG */}

                    <div className="row g-3 mx-2 px-5 mt-4">
                        <div className="col-md-12">
                            <h6>Pre Chemotherapy Check Up data submission for {formatDate(chemoSessionDate)} session</h6>
                            <hr />
                        </div>
                        <div className="col-md-3 col-12">
                            <TextField
                                label={
                                    <>
                                        Date of Check Up <span className="txtRed">*</span>
                                    </>
                                }
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                name="checkupDate"
                                value={values?.checkupDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(errors?.checkupDate && touched?.checkupDate)}
                                helperText={touched?.checkupDate && errors?.checkupDate}
                            />
                        </div>
                        <div className="col-md-3 col-12">
                            <TextField
                                label={
                                    <>
                                        ECOG PS (0/1/2/3/4/5) <span className="txtRed">*</span>
                                    </>
                                }
                                fullWidth
                                name="ecogStatus"
                                value={values?.ecogStatus}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(errors?.ecogStatus && touched?.ecogStatus)}
                                helperText={touched?.ecogStatus && errors?.ecogStatus}
                                InputLabelProps={{
                                    shrink: Boolean(values?.ecogStatus),
                                }}
                            />
                        </div>
                    </div>

                    {/* Recent Lab Results */}
                    <div className="p-3 px-5 mt-4">
                        <h6 className="mb-3">Recent Lab Results</h6>

                        {labTestLabels.map((test) => (
                            <div className="row g-3 mt-3" key={test?.key}>
                                <div className="col-md-4 col-12">
                                    <TextField
                                        label={test?.label}
                                        placeholder="(Value)"
                                        fullWidth
                                        name={test?.key}
                                        value={values[test?.key] || ""}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        InputLabelProps={{
                                            shrink: Boolean(values[test?.key]),
                                        }}
                                    />
                                </div>
                                <div className="col-md-4 col-12 mx-4">
                                    <TextField
                                        label="DD-MM-YYYY"
                                        type="date"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        name={`${test?.key}Date`}
                                        value={values[`${test?.key}Date`]}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default PreChemoCheckup;